"use client";

import { useState, useEffect, useRef, useMemo } from "react";
import { Box, HStack, Input, Text } from "@chakra-ui/react";
import { LuChevronDown, LuSearch, LuX } from "react-icons/lu";

// Full list of supported countries
const SUPPORTED_COUNTRIES = [
  { code: "us", name: "United States" },
  { code: "gb", name: "United Kingdom" },
  { code: "de", name: "Germany" },
  { code: "fr", name: "France" },
  { code: "es", name: "Spain" },
  { code: "it", name: "Italy" },
  { code: "nl", name: "Netherlands" },
  { code: "be", name: "Belgium" },
  { code: "ch", name: "Switzerland" },
  { code: "at", name: "Austria" },
  { code: "pt", name: "Portugal" },
  { code: "pl", name: "Poland" },
  { code: "se", name: "Sweden" },
  { code: "dk", name: "Denmark" },
  { code: "no", name: "Norway" },
  { code: "fi", name: "Finland" },
  { code: "ie", name: "Ireland" },
  { code: "au", name: "Australia" },
  { code: "ca", name: "Canada" },
  { code: "jp", name: "Japan" },
  { code: "cn", name: "China" },
  { code: "kr", name: "South Korea" },
  { code: "br", name: "Brazil" },
  { code: "mx", name: "Mexico" },
  { code: "ar", name: "Argentina" },
  { code: "in", name: "India" },
  { code: "ru", name: "Russia" },
  { code: "za", name: "South Africa" },
  { code: "ae", name: "United Arab Emirates" },
  { code: "sg", name: "Singapore" },
  { code: "hk", name: "Hong Kong" },
  { code: "nz", name: "New Zealand" },
  { code: "cz", name: "Czech Republic" },
  { code: "hu", name: "Hungary" },
  { code: "gr", name: "Greece" },
  { code: "ro", name: "Romania" },
  { code: "th", name: "Thailand" },
  { code: "vn", name: "Vietnam" },
  { code: "ph", name: "Philippines" },
  { code: "my", name: "Malaysia" },
  { code: "id", name: "Indonesia" },
  { code: "tr", name: "Turkey" },
  { code: "il", name: "Israel" },
  { code: "eg", name: "Egypt" },
  { code: "co", name: "Colombia" },
  { code: "cl", name: "Chile" },
  { code: "pe", name: "Peru" },
];

interface CountrySelectorProps {
  /** Currently selected country code (lowercase) */
  value: string;
  /** Callback when country changes */
  onChange: (code: string) => void;
  /** Whether the selector is disabled */
  disabled?: boolean;
  /** Placeholder text when no country is selected */
  placeholder?: string;
  /** Whether to show the clear button */
  allowClear?: boolean;
  /** Size variant */
  size?: "sm" | "md";
}

export function CountrySelector({
  value,
  onChange,
  disabled = false,
  placeholder = "Select country",
  allowClear = true,
  size = "md",
}: CountrySelectorProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const containerRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  const height = size === "sm" ? "36px" : "44px";
  const fontSize = size === "sm" ? "13px" : "14px";

  // Get selected country name
  const selectedCountry = useMemo(() => {
    if (!value) return null;
    return SUPPORTED_COUNTRIES.find(
      (c) => c.code.toLowerCase() === value.toLowerCase()
    );
  }, [value]);

  // Filter countries based on search
  const filteredCountries = useMemo(() => {
    if (!searchQuery) return SUPPORTED_COUNTRIES;
    const query = searchQuery.toLowerCase();
    return SUPPORTED_COUNTRIES.filter(
      (c) =>
        c.name.toLowerCase().includes(query) ||
        c.code.toLowerCase().includes(query)
    );
  }, [searchQuery]);

  // Close dropdown on click outside
  useEffect(() => {
    if (!isOpen) {
      setSearchQuery("");
      return;
    }

    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen]);

  // Focus search input when dropdown opens
  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      setTimeout(() => searchInputRef.current?.focus(), 0);
    }
  }, [isOpen]);

  const handleSelect = (code: string) => {
    onChange(code.toLowerCase());
    setIsOpen(false);
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange("");
  };

  return (
    <Box position="relative" ref={containerRef}>
      {/* Trigger Button */}
      <Box
        as="button"
        onClick={(e: React.MouseEvent) => {
          e.preventDefault();
          if (!disabled) setIsOpen(!isOpen);
        }}
        h={height}
        w="100%"
        bg="white"
        border="0.5px solid var(--color-border)"
        borderRadius="8px"
        px={3}
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        cursor={disabled ? "not-allowed" : "pointer"}
        opacity={disabled ? 0.6 : 1}
        transition="all 150ms ease"
        _hover={{
          borderColor: disabled ? "var(--color-border)" : "var(--color-border-strong)",
        }}
      >
        <Text
          fontSize={fontSize}
          color={selectedCountry ? "var(--color-text-primary)" : "var(--color-text-tertiary)"}
        >
          {selectedCountry?.name || placeholder}
        </Text>
        <HStack gap={1}>
          {allowClear && value && (
            <Box
              as="span"
              onClick={handleClear}
              p={1}
              borderRadius="4px"
              display="flex"
              alignItems="center"
              justifyContent="center"
              _hover={{ bg: "var(--color-surface)" }}
            >
              <LuX size={14} color="var(--color-text-tertiary)" />
            </Box>
          )}
          <LuChevronDown
            size={16}
            color="var(--color-text-tertiary)"
            style={{
              transform: isOpen ? "rotate(180deg)" : "rotate(0deg)",
              transition: "transform 0.15s",
            }}
          />
        </HStack>
      </Box>

      {/* Dropdown */}
      {isOpen && (
        <Box
          position="absolute"
          top="calc(100% + 4px)"
          left={0}
          right={0}
          bg="white"
          border="0.5px solid var(--color-border)"
          borderRadius="8px"
          boxShadow="0 8px 24px rgba(0, 0, 0, 0.12)"
          zIndex={100}
          overflow="hidden"
        >
          {/* Search */}
          <Box p={3} borderBottom="0.5px solid var(--color-border)">
            <HStack
              bg="var(--color-surface)"
              borderRadius="6px"
              border="0.5px solid var(--color-border)"
              px={3}
              h="36px"
            >
              <LuSearch size={14} color="var(--color-text-tertiary)" />
              <Input
                ref={searchInputRef}
                placeholder="Search countries..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                border="none"
                h="100%"
                p={0}
                fontSize="13px"
                bg="transparent"
                inputMode="search"
                _focus={{ boxShadow: "none", outline: "none" }}
                _placeholder={{ color: "var(--color-text-tertiary)" }}
              />
            </HStack>
          </Box>

          {/* Options */}
          <Box maxH="240px" overflowY="auto">
            {filteredCountries.map((country) => (
              <Box
                key={country.code}
                as="button"
                w="100%"
                px={4}
                py={2.5}
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                bg={
                  value.toLowerCase() === country.code.toLowerCase()
                    ? "var(--color-primary-subtle)"
                    : "transparent"
                }
                fontSize="13px"
                textAlign="left"
                cursor="pointer"
                _hover={{ bg: "var(--color-primary-subtle)" }}
                onClick={(e: React.MouseEvent) => {
                  e.preventDefault();
                  handleSelect(country.code);
                }}
              >
                <Text
                  fontWeight={
                    value.toLowerCase() === country.code.toLowerCase()
                      ? "500"
                      : "400"
                  }
                >
                  {country.name}
                </Text>
                <Text
                  fontSize="11px"
                  color="var(--color-text-tertiary)"
                  textTransform="uppercase"
                >
                  {country.code}
                </Text>
              </Box>
            ))}

            {filteredCountries.length === 0 && (
              <Box px={4} py={6} textAlign="center">
                <Text fontSize="13px" color="var(--color-text-tertiary)">
                  No countries found
                </Text>
              </Box>
            )}
          </Box>
        </Box>
      )}
    </Box>
  );
}

/** Helper to get country name from code */
export function getCountryName(code: string): string {
  const country = SUPPORTED_COUNTRIES.find(
    (c) => c.code.toLowerCase() === code.toLowerCase()
  );
  return country?.name || code.toUpperCase();
}

/** Export country list for use elsewhere */
export { SUPPORTED_COUNTRIES };
